import { ref } from 'vue';
import { defineStore } from 'pinia';

import type { DateWithItinerary } from '@apps/main/new-desktop/core/stores/trip/tripStore.model';
import { useItineraryStore } from '@apps/main/new-desktop/core/stores/itinerary/itineraryStore';

import { SemboDate, daysInMonth } from '@libs/sembo-dates';
import type { MonthIndex } from '@libs/sembo-dates';
import { normalizeDate, readonlyRef } from '@libs/utils';

import { useCustomerFlowStore } from '../customerFlowStore';

import {
  getStartSearchDateFromPreferredDay,
  getStartSearchDateWithoutPreferredDay,
  getEndSearchDateFromPreferredDay,
  getEndSearchDateWithoutPreferredDay,
} from './preferredTimeStoreLogic';

export const usePreferredTimeStore = defineStore('PreferredTimeStore', () => {
  const itineraryStore = useItineraryStore();
  const customerFlowStore = useCustomerFlowStore();

  // State
  const preferredDay = ref<Date | undefined>(undefined);
  const preferredMonth = ref<MonthIndex | undefined>(undefined);
  const startSearchDate = ref<Date | undefined>(undefined);
  const endSearchDate = ref<Date | undefined>(undefined);

  const expandedMonth = ref<Date | undefined>(startSearchDate.value);

  // Actions
  function setPreferredDay(day: Date | undefined): void {
    preferredDay.value = day;
  }

  function clearPreferredDay(): void {
    preferredDay.value = undefined;
    if (customerFlowStore.isFlightOnlyFromMarketplaceFlow) {
      customerFlowStore.resetCustomerFlow();
    }
  }

  function setInitialValueForDayAndMonth(
    day: Date | undefined,
    month: MonthIndex | undefined
  ): void {
    preferredDay.value = day;
    preferredMonth.value = month;

    const today = new SemboDate().startOf('day');

    if (preferredDay.value && preferredDay.value < today.date) {
      preferredDay.value = undefined;
    }

    startSearchDate.value = preferredDay.value
      ? getStartSearchDateFromPreferredDay(preferredDay.value)
      : getStartSearchDateWithoutPreferredDay(preferredMonth.value);
    endSearchDate.value = preferredDay.value
      ? getEndSearchDateFromPreferredDay(preferredDay.value)
      : getEndSearchDateWithoutPreferredDay(preferredMonth.value);

    if (startSearchDate.value) {
      expandMonth(startSearchDate.value);
    }
  }

  function expandMonth(month: Date): void {
    expandedMonth.value = isDateInExpandedMonth(month)
      ? undefined
      : new SemboDate(month).get();

    if (expandedMonth.value) {
      itineraryStore.itineraryDates = getMonthDates(expandedMonth.value);
    }
  }

  function getMonthDates(month: Date): DateWithItinerary[] {
    const result: DateWithItinerary[] = [];

    const isCurrentMonth = new SemboDate().isSameMonth(month);

    const remainingDatesInCurrentMonth = isCurrentMonth
      ? daysInMonth(new Date()) - new Date().getDate()
      : daysInMonth(month) - 1;

    const initialDate = isCurrentMonth
      ? new Date()
      : new SemboDate(month).startOf('month').get();

    for (let index = 0; index <= remainingDatesInCurrentMonth; index++) {
      const date = normalizeDate(
        new SemboDate(initialDate).add({ days: index }).get()
      );
      const foundItineraryInDate = itineraryStore.itineraries.find(
        (itinerary) => new SemboDate(itinerary.date).isSameDay(date)
      );
      result.push({
        date,
        itinerary: foundItineraryInDate,
      });
    }

    return result;
  }

  function isDateInExpandedMonth(month: Date): boolean {
    return (
      !!expandedMonth.value &&
      new SemboDate(expandedMonth.value).isSameMonth(month)
    );
  }

  function resetStore(): void {
    preferredDay.value = undefined;
    preferredMonth.value = undefined;
    startSearchDate.value = undefined;
    endSearchDate.value = undefined;
    expandedMonth.value = startSearchDate.value;
  }

  return {
    // State
    preferredDay: readonlyRef(preferredDay),
    preferredMonth: readonlyRef(preferredMonth),
    startSearchDate,
    endSearchDate,
    expandedMonth,

    // Actions
    setInitialValueForDayAndMonth,
    setPreferredDay,
    clearPreferredDay,
    expandMonth,
    isDateInExpandedMonth,
    resetStore,
  };
});
